import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Upload, Loader2, X, AlertTriangle, Database, ShoppingCart, CreditCard, Package, Store, Calendar, RotateCcw, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { LinkedConnector } from '@/types';
import { StandardizedConnection } from '@/types/connections';
import { saveFormDraft } from '@/utils/organization/saveOrganization';
import { noteUnsavedConnectionIds } from '@/utils/onboarding/sessionState';
import { draftTokenField } from '@/utils/onboarding/draftToken';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { parseCSVLine } from '@/utils/csvApi';
import {
  mergeThousandsIfLastColumn,
  normalizeInventoryCsvRow,
  parseInventoryQuantity,
  trimTrailingEmpty,
} from '@/utils/csvQuantity';
import { useRunSalesReconciliation } from '@/hooks/useRunSalesReconciliation';
import { useInventoryLedgerSources } from '@/hooks/useInventoryLedgerSources';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  findInventoryIdentifierColumn,
  missingInventoryIdentifierMessage,
  readCsvText,
  validateInventoryCsvFile,
} from '@/shared/identifierResolution/findInventoryIdentifierColumn';
import {
  customCanonicalMap,
  customNamesFromRows,
  useOrganisationCustomIdentifiers,
} from '@/hooks/useOrganisationCustomIdentifiers';

type ImportType = 'ecommerce' | 'banking' | 'accounting';
type EcommerceSubtype = 'amazon' | 'shopify';
type EcommerceDataKind = 'sales' | 'inventory';

interface ImportTypeOption {
  id: ImportType;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
}

interface EcommerceSubtypeOption {
  id: EcommerceSubtype;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
}

interface EcommerceDataKindOption {
  id: EcommerceDataKind;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
}

interface UnifiedCsvImportProps {
  onSuccess: (connector: LinkedConnector | StandardizedConnection) => Promise<void>;
  onImportStart?: () => void;
  /** Called when an import stops running, whether it succeeded, failed or was cancelled. */
  onImportFinished?: () => void;
  isConnecting?: boolean;
  shouldSaveDraft?: boolean;
  mode?: 'connector' | 'standard'; // connector mode returns LinkedConnector, standard mode returns StandardizedConnection
  preSelectedFile?: File; // Pre-selected CSV file
  organizationId?: string; // Pass organization ID from parent context
}

const getCompanyCsvConnectionId = (): string => {
  const companyData = sessionStorage.getItem('figabl_company_data');
  if (companyData) {
    try {
      const parsedData = JSON.parse(companyData);

      for (const connectionId in parsedData?.connections || {}) {
        if (connectionId.startsWith('csv-import-')) {
          return connectionId;
        }
      }
    } catch (e) {
      console.error('Error parsing company data:', e);
    }
  }

  return `csv-import-${Date.now()}`;
};

// Clean CSV by removing metadata lines before upload
// Only reads the first 10KB to detect metadata headers, avoiding loading the entire file into memory
const cleanCsvFile = async (file: File): Promise<{ cleanedFile: File, linesRemoved: number }> => {
  // Read only the first 10KB to detect metadata lines
  const headerChunk = await file.slice(0, 10240).text();
  const lines = headerChunk.split(/\r?\n/);

  // Detect header line: find first line with many comma-separated fields
  // Amazon metadata rows may contain commas but have few fields (1-3)
  // The actual header row has 7+ fields (date/time, settlement id, type, order id, sku, etc.)
  let headerLineIndex = -1;

  for (let i = 0; i < Math.min(20, lines.length); i++) {
    const line = lines[i].trim();
    const fieldCount = line.split(',').length;
    if (fieldCount >= 7) {
      headerLineIndex = i;
      break;
    }
  }

  // If we found metadata lines, slice the file at the byte offset without loading full content
  if (headerLineIndex > 0) {
    const encoder = new TextEncoder();
    let byteOffset = 0;
    for (let i = 0; i < headerLineIndex; i++) {
      // Account for the line content plus the newline character
      byteOffset += encoder.encode(lines[i]).length + 1;
    }
    // Handle \r\n line endings: check if original chunk uses them
    if (headerChunk.includes('\r\n')) {
      // Recalculate with \r\n
      byteOffset = 0;
      for (let i = 0; i < headerLineIndex; i++) {
        byteOffset += encoder.encode(lines[i]).length + 2; // +2 for \r\n
      }
    }
    const cleanedFile = new File([file.slice(byteOffset)], file.name, { type: file.type });
    return { cleanedFile, linesRemoved: headerLineIndex };
  }

  // No metadata found, return original
  return { cleanedFile: file, linesRemoved: 0 };
};

const uploadFileInChunks = async (file: File, fileName: string, chunkSize: number, onProgress?: (progress: number) => void) => {
  const totalChunks = Math.ceil(file.size / chunkSize);
  const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    const chunkFileName = `${fileName}.part${i.toString().padStart(3, '0')}`;

    // Report progress at start of chunk (more frequent updates)
    if (onProgress) {
      const startProgress = Math.round((i / totalChunks) * 5);
      onProgress(startProgress);
    }

    const { error: chunkError } = await supabase.storage
      .from('company-files')
      .upload(chunkFileName, chunk, {
        cacheControl: '3600',
        upsert: false
      });

    if (chunkError) {
      throw new Error(`Failed to upload chunk ${i + 1}: ${chunkError.message}`);
    }

    // Report upload progress after each chunk completes (0-5% of total)
    if (onProgress) {
      const completedProgress = Math.round(((i + 1) / totalChunks) * 5);
      onProgress(completedProgress);
    }
  }

  // Store metadata about the chunked upload
  const { error: metaError } = await supabase.storage
    .from('company-files')
    .upload(`${fileName}.meta`, new Blob([JSON.stringify({
      originalName: file.name,
      totalSize: file.size,
      totalChunks,
      uploadId,
      chunkSize
    })], { type: 'application/json' }), {
      cacheControl: '3600',
      upsert: false
    });

  if (metaError) {
    throw new Error(`Failed to upload metadata: ${metaError.message}`);
  }

  return { path: fileName };
};

const importTypeOptions: ImportTypeOption[] = [
  {
    id: 'ecommerce',
    name: 'E-commerce Data',
    description: 'Amazon, Shopify, or other online store data',
    icon: ShoppingCart
  },
  {
    id: 'banking',
    name: 'Banking Data',
    description: 'Bank statements, transactions, and financial data',
    icon: CreditCard
  },
  {
    id: 'accounting',
    name: 'Accounting Data',
    description: 'Bookkeeping, invoices, and accounting records',
    icon: Database
  }
];

const ecommerceSubtypeOptions: EcommerceSubtypeOption[] = [
  {
    id: 'amazon',
    name: 'Amazon Data',
    description: 'Amazon marketplace sales and inventory data',
    icon: Package
  },
  {
    id: 'shopify',
    name: 'Shopify Data',
    description: 'Shopify store sales and customer data',
    icon: Store
  }
];

const ecommerceDataKindOptions: EcommerceDataKindOption[] = [
  {
    id: 'sales',
    name: 'Transactions Data',
    description: 'Sales, returns, fees, liquidations, and adjustments',
    icon: ShoppingCart
  },
  {
    id: 'inventory',
    name: 'Inventory Data',
    description: 'Current stock levels (requires SKU and Quantity columns)',
    icon: Package
  }
];

const UnifiedCsvImport: React.FC<UnifiedCsvImportProps> = ({
  onSuccess,
  onImportStart,
  onImportFinished,
  isConnecting = false,
  shouldSaveDraft = true,
  mode = 'connector',
  preSelectedFile,
  organizationId
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const customIdentifiersQuery = useOrganisationCustomIdentifiers(organizationId);
  const customNames = useMemo(
    () => customNamesFromRows(customIdentifiersQuery.data),
    [customIdentifiersQuery.data],
  );
  const customCanonicalByName = useMemo(
    () => customCanonicalMap(customIdentifiersQuery.data),
    [customIdentifiersQuery.data],
  );

  // Refs for scrolling
  const ecommerceSubtypeRef = useRef<HTMLDivElement>(null);
  const ecommerceDataKindRef = useRef<HTMLDivElement>(null);
  const readyToImportRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  const [csvFile, setCsvFile] = useState<File | null>(preSelectedFile || null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importType, setImportType] = useState<ImportType | null>(null);
  const [ecommerceSubtype, setEcommerceSubtype] = useState<EcommerceSubtype | null>(null);
  const [ecommerceDataKind, setEcommerceDataKind] = useState<EcommerceDataKind | null>(null);
  const [inventoryDate, setInventoryDate] = useState<Date>(new Date());
  const [inventoryColumnsValid, setInventoryColumnsValid] = useState<boolean | null>(null);
  const [inventoryValidationError, setInventoryValidationError] = useState<string | null>(null);
  const [salesColumnsValid, setSalesColumnsValid] = useState<boolean | null>(null);
  const [salesValidationError, setSalesValidationError] = useState<string | null>(null);
  const [inventoryDatePickerOpen, setInventoryDatePickerOpen] = useState(false);
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [showEcommerceSubtype, setShowEcommerceSubtype] = useState(false);
  const [showEcommerceDataKind, setShowEcommerceDataKind] = useState(false);
  const [baseConnectionId] = useState(() => {
    // If we have a preSelectedFile, this is a NEW import - generate fresh ID
    if (preSelectedFile) {
      const newId = `csv-import-${Date.now()}`;
      const storedCsvConnections = sessionStorage.getItem('figabl_csv_connection_ids') || '[]';
      try {
        const csvConnections = JSON.parse(storedCsvConnections);
        csvConnections.push(newId);
        sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csvConnections));
      } catch {
        sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify([newId]));
      }
      noteUnsavedConnectionIds([newId]);
      return newId;
    }

    // No preSelectedFile - check for existing in-progress import
    const storedCsvConnections = sessionStorage.getItem('figabl_csv_connection_ids') || '[]';
    try {
      const csvConnections = JSON.parse(storedCsvConnections);
      return csvConnections.length > 0 ? csvConnections[csvConnections.length - 1] : getCompanyCsvConnectionId();
    } catch {
      return getCompanyCsvConnectionId();
    }
  });
  // Start with false - checkExistingImport will restore if there's a valid in-progress job
  const [isImporting, setIsImporting] = useState<boolean>(false);

  // Tell the parent when a running import stops, so it can re-enable submit
  // after a failure or cancel as well as after success.
  const wasImporting = useRef(false);
  useEffect(() => {
    if (wasImporting.current && !isImporting) onImportFinished?.();
    wasImporting.current = isImporting;
  }, [isImporting, onImportFinished]);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [lastImportedOrganisationId, setLastImportedOrganisationId] = useState<string | null>(organizationId || null);
  // Start with null - no progress indicator until we confirm there's an active import
  const [importProgress, setImportProgress] = useState<number | null>(null);
  const { run: runSalesReconciliation } = useRunSalesReconciliation();
  const [reconciliationSource, setReconciliationSource] = useState<string>('');

  const reconciliationSourcesEnabled =
    importSuccess &&
    importType === 'ecommerce' &&
    ecommerceDataKind === 'sales' &&
    Boolean(lastImportedOrganisationId);

  const { data: ledgerSources = [], isLoading: ledgerSourcesLoading } = useInventoryLedgerSources(
    lastImportedOrganisationId ?? undefined,
    reconciliationSourcesEnabled,
  );

  const salesReconciliationEligibleSources = useMemo(
    () => ledgerSources.filter((s) => s.sales_deductions_allowed && s.source !== "manual_received"),
    [ledgerSources],
  );

  useEffect(() => {
    if (!reconciliationSourcesEnabled) {
      setReconciliationSource('');
      return;
    }
    const eligible = salesReconciliationEligibleSources;
    if (eligible.length === 0) return;
    const wanted = ecommerceSubtype ? `${ecommerceSubtype}_csv_import`.toLowerCase() : '';
    const match = eligible.find((s) => s.source.toLowerCase() === wanted);
    setReconciliationSource((prev) => {
      if (prev && eligible.some((e) => e.source === prev)) return prev;
      return (match ?? eligible[0]).source;
    });
  }, [reconciliationSourcesEnabled, salesReconciliationEligibleSources, ecommerceSubtype]);

  // Shared function to map database progress to display progress
  const mapProgressToDisplay = (dbProgress: number | null): number => {
    if (!dbProgress) return 5;
    // Processing phase always shows minimum 5% since upload is complete
    return Math.max(5, Math.round(dbProgress));
  };

  // Persist progress to sessionStorage (only if it's a real number)
  useEffect(() => {
    if (importProgress !== null) {
      sessionStorage.setItem(`csv_import_progress_${baseConnectionId}`, importProgress.toString());
    }
  }, [importProgress, baseConnectionId]);

  // Check for existing import job on mount
  useEffect(() => {
    const checkExistingImport = async () => {
      const savedJobId = sessionStorage.getItem(`csv_import_job_${baseConnectionId}`);
      const storedFileName = sessionStorage.getItem(`csv_import_file_name_${baseConnectionId}`);

      if (savedJobId) {
        setImportJobId(savedJobId);

        // Check job status
        const { data: existingJob } = await (supabase
          .from('csv_import_jobs') as any)
          .select('status, progress_percentage, error_message')
          .eq('id', savedJobId)
          .maybeSingle();

        if (existingJob) {
          if (existingJob.status === 'processing' || existingJob.status === 'queued') {
            setIsImporting(true);
            setImportProgress(mapProgressToDisplay(existingJob.progress_percentage));
            // Set up real-time subscription for existing job
            setupProgressSubscription(savedJobId);
          } else if (existingJob.status === 'completed') {
            setImportProgress(100);
            // Only set success and call handler if we have a valid stored file name
            // This prevents the 3rd import from immediately showing as complete
            if (storedFileName) {
              setImportSuccess(true);
              handleImportSuccess(storedFileName);
            } else {
              // Clean up if no stored file name
              sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
              sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
              sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
            }
          } else if (existingJob.status === 'failed') {
            // Clean up failed job from session storage
            sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
          }
        } else {
          // No existing job found; enable uploads and cleanup any stale state
          sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
          sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
          // If we have a saved progress or upload-in-progress flag, show progress immediately to avoid 0% flicker
          const savedProgress = parseInt(sessionStorage.getItem(`csv_import_progress_${baseConnectionId}`) || '0');
          const uploadInProgress = sessionStorage.getItem(`csv_upload_in_progress_${baseConnectionId}`) === 'true';
          if ((savedProgress > 0 && savedProgress < 100) || uploadInProgress) {
            setIsImporting(true);
            setImportProgress(savedProgress);
          } else {
            sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
            setIsImporting(false);
          }
        }
      } else {
        // No saved job id - clean up any stale state
        sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
        setIsImporting(false);
        setImportProgress(null);
      }
    };

    checkExistingImport();
  }, [baseConnectionId]);

  // Cleanup sessionStorage on unmount after successful import
  useEffect(() => {
    return () => {
      if (importSuccess) {
        sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
      }
    };
  }, [importSuccess, baseConnectionId]);

  const setupProgressSubscription = (jobId: string) => {
    const channel = supabase
      .channel(`csv_import_${jobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'csv_import_jobs',
          filter: `id=eq.${jobId}`
        },
        (payload) => {
          const job = payload.new;
          setImportProgress(mapProgressToDisplay(job.progress_percentage));

          if (job.status === 'completed') {
            setImportProgress(100);
            handleImportSuccess(csvFile?.name || 'file', String(job.id));
            channel.unsubscribe();
            // Clean up session storage on completion
            sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
          } else if (job.status === 'failed') {
            setFileError(job.error_message || 'Import failed');
            toast({
              title: 'Import Failed',
              description: job.error_message || 'Import failed',
              variant: 'destructive'
            });
            setIsImporting(false);
            channel.unsubscribe();
            // Clean up session storage on failure
            sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
            sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
          }
        }
      )
      .subscribe();

    // Cleanup subscription after 10 minutes
    setTimeout(() => {
      channel.unsubscribe();
    }, 600000);

    return channel;
  };

  // Handle pre-selected file
  useEffect(() => {
    if (preSelectedFile && !showTypeSelection) {
      setShowTypeSelection(true);
    }
  }, [preSelectedFile, showTypeSelection]);

  // Re-validate inventory headers after org custom identifiers settle (or when none exist).
  useEffect(() => {
    if (ecommerceDataKind !== 'inventory' || !csvFile) return;
    if (organizationId && customIdentifiersQuery.isLoading) return;
    let cancelled = false;
    void (async () => {
      const result = await validateInventoryCsvFile(csvFile, {
        customNames,
        customCanonicalByName,
      });
      if (cancelled) return;
      setInventoryColumnsValid(result.valid);
      setInventoryValidationError(result.valid ? null : result.error || 'Invalid CSV format');
    })();
    return () => {
      cancelled = true;
    };
  }, [
    ecommerceDataKind,
    csvFile,
    customNames,
    customCanonicalByName,
    customIdentifiersQuery.isLoading,
    organizationId,
  ]);

  // Auto-scroll when import starts
  useEffect(() => {
    if (isImporting) {
      setTimeout(() => {
        progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isImporting]);

  // Polling fallback for progress updates (in case realtime pauses after navigation)
  useEffect(() => {
    if (!importJobId || !isImporting) return;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = window.setInterval(async () => {
      const { data: job } = await (supabase
        .from('csv_import_jobs') as any)
        .select('status, progress_percentage, error_message')
        .eq('id', importJobId)
        .maybeSingle();

      if (!job) return;

      setImportProgress(mapProgressToDisplay(job.progress_percentage));

      if (job.status === 'completed') {
        setImportProgress(100);
        handleImportSuccess(csvFile?.name || 'file', importJobId);
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
      } else if (job.status === 'failed') {
        setFileError(job.error_message || 'Import failed');
        toast({
          title: 'Import Failed',
          description: job.error_message || 'Import failed',
          variant: 'destructive'
        });
        setIsImporting(false);
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
        sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
      }
    }, 2000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [importJobId, isImporting, baseConnectionId, csvFile?.name, toast]);

  const handleCsvFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    setImportSuccess(false);
    setReconciliationSource('');

    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      const isXlsx = /\.xlsx?$/i.test(file.name.toLowerCase());
      const isCsv = file.name.toLowerCase().endsWith('.csv');

      if (!isCsv && !isXlsx) {
        setFileError('Please select a valid CSV or Excel (.xlsx/.xls) file');
        return;
      }

      // Check for empty file
      if (file.size === 0) {
        setFileError('The selected file is empty. Please select a valid file with data.');
        return;
      }

      if (file.size > 500 * 1024 * 1024) {
        setFileError('File size exceeds 500MB limit. Please use a smaller file or split your data into multiple files.');
        return;
      }

      if (isXlsx) {
        // Convert XLSX/XLS to CSV using exceljs (Apache-2.0, avoids CVE-2023-30533 in xlsx@0.18.5).
        try {
          const ExcelJS = await import('exceljs');
          const arrayBuffer = await file.arrayBuffer();
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);
          const firstSheet = workbook.worksheets[0];
          const rows: string[] = [];
          firstSheet.eachRow(row => {
            const cells = (row.values as unknown[]).slice(1);
            rows.push(cells.map(c => {
              const val = c == null ? '' : String(c);
              return val.includes(',') || val.includes('"') || val.includes('\n')
                ? `"${val.replace(/"/g, '""')}"` : val;
            }).join(','));
          });
          const csvContent = rows.join('\n');

          if (!csvContent.trim()) {
            setFileError('The Excel file contains no data. Please select a file with valid content.');
            return;
          }

          // Create a CSV File object from the converted content
          const csvFileName = file.name.replace(/\.xlsx?$/i, '.csv');
          const csvBlob = new Blob([csvContent], { type: 'text/csv' });
          const csvFile = new File([csvBlob], csvFileName, { type: 'text/csv' });

          setCsvFile(csvFile);
          setShowTypeSelection(true);
        } catch (err) {
          console.error('XLSX parse error:', err);
          setFileError('Failed to read the Excel file. Please ensure it is a valid .xlsx or .xls file.');
        }
        return;
      }

      // CSV validation path
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        const firstBytes = content?.slice(0, 500) || '';

        const hasBinaryContent = /[\x00-\x08\x0E-\x1F]/.test(firstBytes);
        if (hasBinaryContent) {
          setFileError('The selected file appears to be corrupted or is not a valid CSV file.');
          return;
        }

        const lines = content?.split(/\r?\n/).filter(line => line.trim().length > 0) || [];
        if (lines.length === 0) {
          setFileError('The CSV file contains no data. Please select a file with valid content.');
          return;
        }

        setCsvFile(file);
        setShowTypeSelection(true);
      };

      reader.onerror = () => {
        setFileError('Failed to read the file. Please try again.');
      };

      reader.readAsText(file.slice(0, 10000));
    }
  };

  const ensureOrganisationAndConnection = async (importType: ImportType, ecommerceSubtype: EcommerceSubtype | null) => {
    try {
      let organisationId: string | null = organizationId || null; // Use passed organizationId first
      let companyData: any = {};

      // Get current company data from sessionStorage
      const companyDataJson = sessionStorage.getItem('figabl_company_data');
      if (companyDataJson) {
        companyData = JSON.parse(companyDataJson);
        if (!organisationId) {
          organisationId = companyData.organization_id || companyData.organisation_id || null;
        }
      }

      // Each CSV import gets its own unique connection ID - do not reuse existing ones
      const connectionId = baseConnectionId;

      // Note: We do NOT write to organisations.connections here.
      // The onSuccess callback chain handles the single DB write to avoid duplicates.

      // Update session storage
      if (!companyData.connections) {
        companyData.connections = [];
      }

      // Update or add connection in session storage
      const sessionConnectionIndex = companyData.connections.findIndex((conn: any) =>
        conn.connectionId === connectionId || conn.id === connectionId
      );

      const sessionConnectionData = {
        id: connectionId,
        connectionId: connectionId,
        name: "CSV Import",
        type: "CSV",
        status: "active",
        created_at: new Date().toISOString(),
        import_type: importType,
        ecommerce_subtype: importType === 'ecommerce' ? ecommerceSubtype : undefined
      };

      if (sessionConnectionIndex >= 0) {
        companyData.connections[sessionConnectionIndex] = sessionConnectionData;
      } else {
        companyData.connections.push(sessionConnectionData);
      }

      sessionStorage.setItem('figabl_company_data', JSON.stringify(companyData));

      // Save draft if user authenticated
      if (user && shouldSaveDraft) {
        await saveFormDraft(companyData);
      }

      return { organisationId, connectionId, companyData };
    } catch (error) {
      console.error('Error ensuring organisation and connection:', error);
      throw error;
    }
  };

  const handleImport = async () => {
    if (!csvFile) {
      toast({
        title: 'Error',
        description: 'Please select a CSV file first',
        variant: 'destructive'
      });
      return;
    }

    if (!importType) {
      toast({
        title: 'Error',
        description: 'Please select the data type for your CSV file',
        variant: 'destructive'
      });
      return;
    }

    if (importType === 'ecommerce' && !ecommerceSubtype) {
      toast({
        title: 'Error',
        description: 'Please select the e-commerce platform for your data',
        variant: 'destructive'
      });
      return;
    }

    if (importType === 'ecommerce' && !ecommerceDataKind) {
      toast({
        title: 'Error',
        description: 'Please select whether this is sales or inventory data',
        variant: 'destructive'
      });
      return;
    }

    // Handle inventory import separately
    if (importType === 'ecommerce' && ecommerceDataKind === 'inventory') {
      await handleInventoryImport();
      return;
    }

    setIsImporting(true);
    setFileError(null);
    setImportProgress(0); // Start new imports at 0%
    sessionStorage.setItem(`csv_upload_in_progress_${baseConnectionId}`, 'true');
    // Store file name for restoration after modal close/reopen
    sessionStorage.setItem(`csv_import_file_name_${baseConnectionId}`, csvFile.name);
    onImportStart?.();

    try {
      // Clean CSV before upload to remove metadata lines
      const { cleanedFile, linesRemoved } = await cleanCsvFile(csvFile);

      // Ensure organisation exists and get proper connection_id BEFORE upload
      const { organisationId, connectionId } = await ensureOrganisationAndConnection(importType, ecommerceSubtype);
      setLastImportedOrganisationId(organisationId || null);

      // Generate timestamp once to avoid duplicate uploads
      const timestamp = Date.now();
      const fileName = `csv-imports/${connectionId}/${timestamp}_${cleanedFile.name}`;
      const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks

      let uploadData;
      if (cleanedFile.size > CHUNK_SIZE) {
        // Use chunked upload for large files with progress tracking
        uploadData = await uploadFileInChunks(cleanedFile, fileName, CHUNK_SIZE, (progress) => {
          setImportProgress(progress); // 0-5% for upload
        });
      } else {
        // Use regular upload for smaller files
        setImportProgress(3); // Set to mid-point for small files
        const { data, error: uploadError } = await supabase.storage
          .from('company-files')
          .upload(fileName, cleanedFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw new Error(`Failed to upload file: ${uploadError.message}`);
        }
        uploadData = data;
        setImportProgress(5); // Complete upload phase
      }

      // Create import job record
      // UC-163 AC-UC163-7/8: Set source_type = 'direct_upload' for CSV uploads per HD-1
      const { data: jobData, error: jobError } = await (supabase
        .from('csv_import_jobs') as any)
        .insert({
          connection_id: connectionId,
          file_name: cleanedFile.name,
          file_size: cleanedFile.size,
          storage_path: uploadData.path,
          status: 'queued',
          import_type: importType,
          ecommerce_subtype: importType === 'ecommerce' ? ecommerceSubtype : null,
          organisation_id: organisationId,
          source_type: 'direct_upload'
        })
        .select()
        .single();

      if (jobError) {
        throw new Error(`Failed to create import job: ${jobError.message}`);
      }

      setImportJobId(jobData.id);

      // Store job ID in session storage for persistence
      sessionStorage.setItem(`csv_import_job_${baseConnectionId}`, jobData.id);
      // Upload phase completed
      sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);

      // Start background processing
      const { data: processData, error: processError } = await invokeFunction(supabase, 'process-csv-import-v3', {
        body: { jobId: jobData.id, ...draftTokenField(organisationId) }
      });

      if (processError) {
        // Extract user-friendly message from edge function response
        let friendlyMessage = 'Unable to process your CSV file. Please try again.';

        if (processError.context?.body) {
          try {
            const errorBody = JSON.parse(processError.context.body);
            if (errorBody.error) {
              friendlyMessage = errorBody.error;
            }
          } catch {
            // JSON parsing failed, use default message
          }
        }

        throw new Error(friendlyMessage);
      }

      // Also check if the function returned a non-success response
      if (processData && processData.error) {
        throw new Error(processData.error);
      }

      // Set up real-time subscription for progress updates
      setupProgressSubscription(jobData.id);

    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to import CSV data';
      setFileError(errorMessage);
      toast({
        title: 'Import Error',
        description: errorMessage,
        variant: 'destructive'
      });
      setIsImporting(false);
      sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
      sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
    }
  };

  const handleInventoryImport = async () => {
    if (!csvFile) return;

    setIsImporting(true);
    setFileError(null);
    setImportProgress(0);
    onImportStart?.();

    try {
      const fileText = await readCsvText(csvFile);
      const headerLines = fileText.split(/\r?\n/).filter(line => line.trim());

      if (headerLines.length < 1) {
        throw new Error('CSV file must contain a header row and at least one data row');
      }

      const rawHeaders = trimTrailingEmpty(parseCSVLine(headerLines[0]));
      const headers = rawHeaders.map(h => h.toLowerCase());
      const headerLength = headers.length;

      const identifierColumn = findInventoryIdentifierColumn(rawHeaders, {
        customNames,
        customCanonicalByName,
      });
      const skuIndex = identifierColumn?.index ?? -1;
      const qtyIndex = headers.findIndex(h => h === 'quantity' || h === 'qty');

      if (skuIndex === -1 || !identifierColumn) {
        throw new Error(missingInventoryIdentifierMessage(customNames));
      }
      if (qtyIndex === -1) {
        throw new Error('CSV must contain a "Quantity" column');
      }

      const identifierType = identifierColumn.identifierType;

      const inventoryItems: { identifier: string; quantity: number }[] = [];
      for (let i = 1; i < headerLines.length; i++) {
        const trimmed = headerLines[i].trim();
        if (!trimmed) continue;

        const values = parseCSVLine(trimmed);
        const row = normalizeInventoryCsvRow(values, headerLength, qtyIndex);
        const identifier = row[skuIndex]?.trim();
        const quantityRaw = mergeThousandsIfLastColumn(row, qtyIndex, headerLength);
        const quantity = parseInventoryQuantity(quantityRaw);

        if (identifier && quantity !== null) {
          inventoryItems.push({ identifier, quantity });
        }
        if (i % 500 === 0) {
          setImportProgress(Math.min(30, Math.round((i / headerLines.length) * 30)));
        }
      }

      if (inventoryItems.length === 0) {
        throw new Error('No valid inventory items found in CSV');
      }

      setImportProgress(30);

      // Get organization ID
      let organisationId = organizationId;
      if (!organisationId) {
        const companyDataJson = sessionStorage.getItem('figabl_company_data');
        if (companyDataJson) {
          const companyData = JSON.parse(companyDataJson);
          organisationId = companyData.organization_id || companyData.organisation_id;
        }
      }

      if (!organisationId) {
        throw new Error('Organization ID not found. Please complete company setup first.');
      }

      setImportProgress(50);

      // Call edge function to import inventory
      const { data, error } = await invokeFunction(supabase, 'import-inventory-csv-v3', {
        body: {
          organisationId,
          snapshotDate: inventoryDate.toISOString().split('T')[0], // Format: YYYY-MM-DD
          inventory: inventoryItems.map(item => ({
            identifier: item.identifier,
            quantity: item.quantity
          })),
          identifierType,
          ecommerceSubtype: ecommerceSubtype, // Pass the e-commerce platform type (amazon/shopify)
          // Post-delivery rework (Bug A2): thread the uploaded file's name
          // through so identifier_observation.source_detail is populated
          // for this ingestion surface, matching background-csv-import-worker-v3.
          fileName: csvFile.name,
          ...draftTokenField(organisationId),
        }
      });

      if (error || data?.error) {
        // Extract friendly error message - prioritize data.error over generic error.message
        let friendlyMessage = 'Failed to import inventory. Please try again.';

        if (data?.error) {
          friendlyMessage = data.error;
        } else if (error?.message && !error.message.includes('non-2xx')) {
          friendlyMessage = error.message;
        }

        throw new Error(friendlyMessage);
      }

      setImportProgress(100);

      // Handle success
      const fileName = csvFile.name;
      const snapshotConnectionId = data?.connectionId;
      setIsImporting(false);
      setImportSuccess(true);

      // Create connection record
      if (mode === 'connector') {
        const snapshotDate = inventoryDate.toISOString().split('T')[0];
        const normalizedDate = snapshotDate.replace(/-/g, '');
        const connectorId = `${baseConnectionId}_inventory_${ecommerceSubtype || 'amazon'}_${normalizedDate}`;
        // Persist snapshot_connection_id to sessionStorage immediately so it survives
        // any state/DB unreliability before finalizeSubmission runs
        if (snapshotConnectionId) {
          sessionStorage.setItem(`snapshot_conn_id_${connectorId}`, snapshotConnectionId);
        }
        const newConnector: LinkedConnector = {
          id: connectorId,
          name: "Inventory CSV Import",
          description: `Inventory file: ${fileName}`,
          logoUrl: 'https://via.placeholder.com/40?text=INV',
          status: "connected",
          connectorId: baseConnectionId,
          connectionId: baseConnectionId,
          service: 'csv',
          file_name: fileName,
          import_type: 'ecommerce',
          ecommerce_subtype: ecommerceSubtype || undefined,
          metadata: {
            snapshot_connection_id: snapshotConnectionId,
            inventory_date: snapshotDate,
            snapshot_date: snapshotDate,
            inventory_source: ecommerceSubtype || undefined,
          }
        };
        await onSuccess(newConnector);
      } else {
        const snapshotDate = inventoryDate.toISOString().split('T')[0];
        const connection: StandardizedConnection = {
          id: baseConnectionId,
          name: `Inventory Import: ${fileName}`,
          status: 'connected',
          logoUrl: "https://via.placeholder.com/40?text=INV",
          service: 'csv',
          connectorId: baseConnectionId,
          description: `Inventory file: ${fileName}`,
          connectionId: baseConnectionId,
          type: 'csv',
          created_at: new Date().toISOString(),
          metadata: {
            file_name: fileName,
            file_size: csvFile.size,
            file_type: 'csv',
            inventory_date: snapshotDate,
            item_count: inventoryItems.length,
            snapshot_connection_id: snapshotConnectionId,
            inventory_source: ecommerceSubtype || undefined,
            snapshot_date: snapshotDate,
          },
          file_name: fileName,
          import_type: 'ecommerce',
          ecommerce_subtype: ecommerceSubtype || undefined
        };
        await onSuccess(connection);
      }

      toast({
        title: 'Inventory Import Successful',
        description: `${inventoryItems.length} inventory items imported for ${format(inventoryDate, 'PPP')}.`,
        variant: 'default'
      });

    } catch (error) {
      console.error('Inventory import error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to import inventory data';
      setFileError(errorMessage);
      toast({
        title: 'Import Error',
        description: errorMessage,
        variant: 'destructive'
      });
      setIsImporting(false);
    }
  };

  const handleImportSuccess = async (fileName: string, jobIdFromEvent?: string | null) => {
    try {
      // Reset importing state immediately to fix button state
      setIsImporting(false);

      const resolvedJobId = jobIdFromEvent ?? importJobId;

      // Clean up session storage on successful completion
      sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
      sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
      sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);
      sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);

      const uniqueCsvImportId = `${baseConnectionId}_${csvFile!.name.replace(/\.[^/.]+$/, "").toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`;

      // Return different connection types based on mode
      if (mode === 'connector') {
        const newConnector: LinkedConnector = {
          id: uniqueCsvImportId,
          name: "CSV Import",
          description: `CSV file: ${fileName}`,
          logoUrl: 'https://via.placeholder.com/40?text=CSV',
          status: "connected",
          connectorId: baseConnectionId,
          connectionId: baseConnectionId,
          service: 'csv',
          file_name: fileName,
          import_type: importType,
          ecommerce_subtype: importType === 'ecommerce' ? ecommerceSubtype : undefined,
          ...(resolvedJobId && {
            metadata: {
              csv_import_job_id: resolvedJobId,
            },
          }),
        };

        await onSuccess(newConnector);
      } else {
        const connection: StandardizedConnection = {
          id: baseConnectionId,
          name: `CSV Import: ${fileName}`,
          status: 'connected',
          logoUrl: "https://via.placeholder.com/40?text=CSV",
          service: 'csv',
          connectorId: baseConnectionId,
          description: `CSV file: ${fileName}`,
          connectionId: baseConnectionId,
          type: 'csv',
          created_at: new Date().toISOString(),
          metadata: {
            file_name: fileName,
            file_size: csvFile!.size,
            file_type: 'csv',
            ...(resolvedJobId && { csv_import_job_id: resolvedJobId }),
          },
          file_name: fileName,
          import_type: importType,
          ecommerce_subtype: importType === 'ecommerce' ? ecommerceSubtype : undefined
        };

        await onSuccess(connection);
      }

      // Only set success state AFTER onSuccess resolves successfully
      setImportSuccess(true);

      toast({
        title: 'Import Successful',
        description: `${fileName} has been imported successfully.`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error handling import success:', error);
      setIsImporting(false);
    }
  };

  const handleCancelImport = () => {
    setCsvFile(null);
    setImportJobId(null);
    setImportProgress(0);
    setImportType(null);
    setEcommerceSubtype(null);
    setEcommerceDataKind(null);
    setShowTypeSelection(false);
    setShowEcommerceSubtype(false);
    setShowEcommerceDataKind(false);
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleTypeSelect = (type: ImportType) => {
    // Allow deselection by clicking the same option
    if (importType === type) {
      setImportType(null);
      setEcommerceSubtype(null);
      setEcommerceDataKind(null);
      setShowEcommerceSubtype(false);
      setShowEcommerceDataKind(false);
      setShowTypeSelection(true);
      return;
    }

    setImportType(type);
    setEcommerceSubtype(null); // Reset subtype when changing main type
    setEcommerceDataKind(null); // Reset data kind

    // If ecommerce is selected, show subtype selection, otherwise hide type selection
    if (type === 'ecommerce') {
      setShowEcommerceSubtype(true);
      setShowEcommerceDataKind(false);
      setShowTypeSelection(false);
      // Scroll to ecommerce subtype section
      setTimeout(() => {
        ecommerceSubtypeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      setShowTypeSelection(false);
      setShowEcommerceSubtype(false);
      setShowEcommerceDataKind(false);
      // Scroll to ready to import section
      setTimeout(() => {
        readyToImportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleEcommerceSubtypeSelect = (subtype: EcommerceSubtype) => {
    // Allow deselection by clicking the same option
    if (ecommerceSubtype === subtype) {
      setEcommerceSubtype(null);
      setEcommerceDataKind(null);
      setShowEcommerceSubtype(true);
      setShowEcommerceDataKind(false);
      return;
    }

    setEcommerceSubtype(subtype);
    setEcommerceDataKind(null);
    setShowEcommerceDataKind(true);
    // Scroll to data kind selection
    setTimeout(() => {
      ecommerceDataKindRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Validate CSV has required Amazon sales columns
  const validateSalesColumns = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    try {
      const content = await file.slice(0, 10240).text();
      const lines = content.split(/\r?\n/).filter(line => line.trim());

      if (lines.length === 0) {
        return { valid: false, error: 'CSV file appears to be empty' };
      }

      // Find the header row (first line with 7+ comma-separated fields)
      let headerLine: string | null = null;
      for (let i = 0; i < Math.min(20, lines.length); i++) {
        const line = lines[i].trim();
        if (line.split(',').length >= 7) {
          headerLine = line;
          break;
        }
      }

      if (!headerLine) {
        return { valid: false, error: 'Could not detect a valid header row. Amazon sales CSV files should contain columns like date/time, type, sku, quantity, and product sales.' };
      }

      const headers = headerLine.split(',').map(h => h.trim().toLowerCase().replace(/"/g, '').replace(/[^a-z0-9]/g, '_'));

      // Known product identifier columns (sanitized names)
      const productIdentifierColumns = ['sku', 'asin', 'upc', 'ean', 'fnsku', 'barcode', 'gtin', 'isbn', 'seller_sku', 'msku', 'merchant_sku', 'item_sku', 'product_sku', 'product_identifier'];
      const hasProductIdentifier = headers.some(h => productIdentifierColumns.includes(h));

      // Required non-identifier columns for Amazon sales reports
      const requiredColumns = ['date_time', 'type', 'quantity', 'product_sales'];
      const missingColumns = requiredColumns.filter(col => !headers.includes(col));

      if (!hasProductIdentifier) {
        missingColumns.unshift('product identifier (sku, asin, upc, etc.)');
      }

      if (missingColumns.length > 0) {
        const readable = missingColumns.map(c => c.replace(/_/g, ' ')).join(', ');
        return { valid: false, error: `CSV is missing required Amazon sales columns: ${readable}. Please upload a valid Amazon transaction report.` };
      }

      return { valid: true };
    } catch {
      return { valid: false, error: 'Failed to read CSV file for validation' };
    }
  };

  const handleEcommerceDataKindSelect = async (dataKind: EcommerceDataKind) => {
    // Allow deselection by clicking the same option
    if (ecommerceDataKind === dataKind) {
      setEcommerceDataKind(null);
      setShowEcommerceDataKind(true);
      setInventoryColumnsValid(null);
      setInventoryValidationError(null);
      setSalesColumnsValid(null);
      setSalesValidationError(null);
      return;
    }

    setEcommerceDataKind(dataKind);

    // Reset validation state
    setInventoryColumnsValid(null);
    setInventoryValidationError(null);
    setSalesColumnsValid(null);
    setSalesValidationError(null);

    // If inventory selected, validate columns immediately once customs are available.
    if (dataKind === 'inventory' && csvFile && !(organizationId && customIdentifiersQuery.isLoading)) {
      const result = await validateInventoryCsvFile(csvFile, {
        customNames,
        customCanonicalByName,
      });
      setInventoryColumnsValid(result.valid);
      if (!result.valid) {
        setInventoryValidationError(result.error || 'Invalid CSV format');
      }
    }

    // If sales selected and Amazon, validate columns immediately
    if (dataKind === 'sales' && ecommerceSubtype === 'amazon' && csvFile) {
      const result = await validateSalesColumns(csvFile);
      setSalesColumnsValid(result.valid);
      if (!result.valid) {
        setSalesValidationError(result.error || 'Invalid CSV format');
      }
    }

    // Scroll to ready to import section
    setTimeout(() => {
      readyToImportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Calculate dynamic height based on current state
  const getScrollAreaHeight = () => {
    if (importSuccess || isImporting) return "h-[400px]";
    if (showEcommerceDataKind) {
      return "h-[550px]";
    }
    if (showEcommerceSubtype) {
      return "h-[500px]";
    }
    if (csvFile && (showTypeSelection || importType)) {
      return "h-[400px]";
    }
    return "h-auto max-h-[300px]";
  };

  return (
    <ScrollArea className={`w-full ${getScrollAreaHeight()}`}>
      <div className="space-y-4 p-4">
        {/* Type Selection */}
        {showTypeSelection && csvFile && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-xl font-medium mb-2">Select Data Type</h3>
              <p className="text-muted-foreground mb-6">
                What type of data does your CSV file contain? This helps us process it correctly.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {importTypeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = importType === option.id;
                return (
                  <Card
                    key={option.id}
                    data-testid={`import-type-${option.id}`}
                    className={`cursor-pointer hover:bg-muted transition-colors ${isSelected ? 'ring-2 ring-primary bg-muted' : ''}`}
                    onClick={() => handleTypeSelect(option.id)}
                  >
                    <CardHeader className="text-center pb-2">
                      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{option.name}</CardTitle>
                      <CardDescription className="text-sm">{option.description}</CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>

          </div>
        )}

        {/* Ecommerce Subtype Selection */}
        {showEcommerceSubtype && csvFile && importType === 'ecommerce' && (
          <div ref={ecommerceSubtypeRef} className="space-y-4">
            <div className="text-center">
              <h3 className="text-xl font-medium mb-2">Select E-commerce Platform</h3>
              <p className="text-gray-500 mb-6">
                Which e-commerce platform is your data from?
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 max-w-md mx-auto">
              {ecommerceSubtypeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = ecommerceSubtype === option.id;
                return (
                  <Card
                    key={option.id}
                    data-testid={`ecommerce-subtype-${option.id}`}
                    className={`cursor-pointer hover:bg-muted transition-colors ${isSelected ? 'ring-2 ring-primary bg-muted' : ''}`}
                    onClick={() => handleEcommerceSubtypeSelect(option.id)}
                  >
                    <CardHeader className="text-center pb-2">
                      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{option.name}</CardTitle>
                      <CardDescription className="text-sm">{option.description}</CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>

          </div>
        )}

        {/* Ecommerce Data Kind Selection (Sales vs Inventory) */}
        {showEcommerceDataKind && csvFile && importType === 'ecommerce' && ecommerceSubtype && (
          <div ref={ecommerceDataKindRef} className="space-y-4">
            <div className="text-center">
              <h3 className="text-xl font-medium mb-2">Select Data Type</h3>
              <p className="text-muted-foreground mb-6">
                What type of {ecommerceSubtype} data does this CSV contain?
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 max-w-md mx-auto">
              {ecommerceDataKindOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = ecommerceDataKind === option.id;
                return (
                  <Card
                    key={option.id}
                    data-testid={`ecommerce-data-kind-${option.id}`}
                    className={`cursor-pointer hover:bg-muted transition-colors ${isSelected ? 'ring-2 ring-primary bg-muted' : ''}`}
                    onClick={() => handleEcommerceDataKindSelect(option.id)}
                  >
                    <CardHeader className="text-center pb-2">
                      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{option.name}</CardTitle>
                      <CardDescription className="text-sm">{option.description}</CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Import CSV Section - Show when type is selected and ready to import */}
        {csvFile && importType && (importType !== 'ecommerce' || (ecommerceSubtype && ecommerceDataKind)) && (
          <div ref={readyToImportRef} className="border border-dashed border-border rounded-lg p-8 flex flex-col items-center justify-center">
            <Upload size={48} className="text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">Ready to Import</h3>
            <p className="text-center text-muted-foreground mb-6 max-w-md">
              Your CSV file is ready to be imported as {importType === 'ecommerce'
                ? `${ecommerceSubtype} ${ecommerceDataKind === 'inventory' ? 'inventory' : 'sales'} data`
                : `${importType} data`}.
            </p>

            {!isImporting && !importSuccess && (
              <div className="w-full max-w-md mb-4">
                <div className="bg-accent/50 rounded-lg p-4 mb-4">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <span className="text-sm font-medium text-foreground shrink-0">Selected file:</span>
                    <span className="text-sm font-medium text-right break-all">{csvFile.name}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <span className="text-sm font-medium text-foreground shrink-0">Data type:</span>
                    <span className="text-sm font-medium">
                      {importType === 'ecommerce'
                        ? `${ecommerceSubtype} ${ecommerceDataKind === 'inventory' ? 'Inventory' : 'Sales/Returns'}`
                        : importType}
                    </span>
                  </div>

                  {/* Inventory Date Picker */}
                  {ecommerceDataKind === 'inventory' && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">Inventory date:</span>
                        <Popover open={inventoryDatePickerOpen} onOpenChange={setInventoryDatePickerOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                "justify-start text-left font-normal",
                                !inventoryDate && "text-muted-foreground"
                              )}
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              {inventoryDate ? format(inventoryDate, "PPP") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 pointer-events-auto" align="end">
                            <CalendarComponent
                              mode="single"
                              selected={inventoryDate}
                              onSelect={(date) => {
                                if (date) {
                                  setInventoryDate(date);
                                  setInventoryDatePickerOpen(false);
                                }
                              }}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      {inventoryColumnsValid === false && (
                        <div className="mt-3">
                          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md">
                            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-red-700 dark:text-red-300">
                              <span className="font-medium">Invalid format:</span> {inventoryValidationError}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            className="w-full mt-2"
                            onClick={handleCancelImport}
                          >
                            Choose Different File
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {salesColumnsValid === false && (
                  <div className="mt-3 mb-3">
                    <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md">
                      <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-700 dark:text-red-300">
                        <span className="font-medium">Invalid format:</span> {salesValidationError}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full mt-2"
                      onClick={handleCancelImport}
                    >
                      Choose Different File
                    </Button>
                  </div>
                )}

                <Button
                  onClick={handleImport}
                  className="w-full"
                  disabled={((isConnecting || isImporting) && !fileError) || (ecommerceDataKind === 'inventory' && inventoryColumnsValid === false) || (ecommerceDataKind === 'sales' && salesColumnsValid === false)}
                  data-testid="start-import-button"
                >
                  {(isConnecting || isImporting) && !fileError ? 'Importing...' : 'Import CSV'}
                </Button>

                {fileError && (
                  <Alert className="mt-3" data-testid="csv-file-error">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-sm">{fileError}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
        )}

        {/* Main upload section - only show when no file selected */}
        {!csvFile && !importSuccess && (
          <div className="border border-dashed border-border rounded-lg p-8 flex flex-col items-center justify-center">
            <Upload size={48} className="text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">Upload CSV or Excel file</h3>
            <p className="text-center text-muted-foreground mb-6 max-w-md">
              Upload a CSV or Excel (.xlsx/.xls) file containing your data.
            </p>

            <div className="w-full max-w-xs mb-6">
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                className="cursor-pointer"
                onChange={handleCsvFileChange}
                disabled={isConnecting || isImporting}
                data-testid="csv-file-input"
              />
              {fileError && (
                <Alert className="mt-2" data-testid="csv-file-error">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">{fileError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* Import Progress */}
        {isImporting && (
          <div ref={progressRef} className="flex flex-col items-center justify-center p-8 space-y-4" data-testid="import-progress-section">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="text-center">
              <span className="text-foreground block">Processing CSV import...</span>
              <div
                className="w-64 bg-muted rounded-full h-2 mt-2"
                role="progressbar"
                aria-valuenow={importProgress || 0}
                aria-valuemin={0}
                aria-valuemax={100}
                data-testid="import-progress"
                data-progress={importProgress || 0}
              >
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${importProgress || 0}%` }}
                />
              </div>
              <span className="text-sm text-muted-foreground mt-1 block">
                {importProgress !== null ? `${importProgress}% complete` : 'Processing...'}
              </span>
            </div>
          </div>
        )}

        {/* Import Success */}
        {importSuccess && (
          <div className="text-center text-green-600 mb-4 space-y-4" data-testid="import-success">
            <div>
              <p className="font-medium">Import successful!</p>
              <p className="text-sm text-muted-foreground mt-1">Your data is now available in the connected data section.</p>
            </div>
            {importType === 'ecommerce' && ecommerceDataKind === 'sales' && importJobId && lastImportedOrganisationId && (
              <div className="w-full max-w-md mx-auto space-y-3 text-left">
                {ledgerSourcesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading inventory sources…</p>
                ) : salesReconciliationEligibleSources.length === 0 ? (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      No CSV ledger source is eligible for sales reconciliation (live sources cannot receive sales
                      deductions). Register a sales CSV import source or contact support.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="reconciliation-source">Deduct sales from ledger source</Label>
                    <Select value={reconciliationSource} onValueChange={setReconciliationSource}>
                      <SelectTrigger id="reconciliation-source">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {salesReconciliationEligibleSources.map((s) => (
                          <SelectItem key={s.source} value={s.source}>
                            {s.display_label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Sales deductions apply only to the selected CSV source. Live connectors already reflect sales.
                    </p>
                  </div>
                )}
                <Button
                  onClick={async () => {
                    try {
                      const result = await runSalesReconciliation.mutateAsync({
                        organisation_id: lastImportedOrganisationId,
                        csv_import_job_id: importJobId,
                        source: reconciliationSource,
                      });
                      toast({
                        title: 'Inventory Reconciliation Complete',
                        description: `Wrote ${result.events_written} sales events across ${result.affected_products} products.`,
                        variant: 'default'
                      });
                    } catch (err) {
                      toast({
                        title: 'Reconciliation Failed',
                        description: err instanceof Error ? err.message : 'Failed to run sales reconciliation',
                        variant: 'destructive',
                      });
                    }
                  }}
                  disabled={
                    runSalesReconciliation.isPending ||
                    ledgerSourcesLoading ||
                    !reconciliationSource ||
                    salesReconciliationEligibleSources.length === 0
                  }
                  variant="secondary"
                  className="w-full"
                >
                  {runSalesReconciliation.isPending ? 'Running reconciliation...' : 'Run inventory reconciliation'}
                </Button>
              </div>
            )}
            <Button
              onClick={() => {
                // Clear all sessionStorage keys related to this import
                sessionStorage.removeItem(`csv_import_job_${baseConnectionId}`);
                sessionStorage.removeItem(`csv_import_file_name_${baseConnectionId}`);
                sessionStorage.removeItem(`csv_import_progress_${baseConnectionId}`);
                sessionStorage.removeItem(`csv_upload_in_progress_${baseConnectionId}`);

                // Reset all component state
                setCsvFile(null);
                setImportSuccess(false);
                setImportType(null);
                setEcommerceSubtype(null);
                setEcommerceDataKind(null);
                setShowTypeSelection(false);
                setShowEcommerceSubtype(false);
                setShowEcommerceDataKind(false);
                setFileError(null);
                setIsImporting(false);
                setImportProgress(null);
                setImportJobId(null);
                setReconciliationSource('');
                setInventoryDate(new Date());

                // Clear any polling intervals
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }

                // Reset file input
                const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                if (fileInput) {
                  fileInput.value = '';
                }
              }}
              variant="outline"
              className="w-full text-foreground"
            >
              Import Another File
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default UnifiedCsvImport;
